import { useEffect } from 'react';
import Header from '../../../components/base/Header';
import Footer from '../../../components/base/Footer';
import { Link } from 'react-router-dom';

export default function AutumnKoyoBlog() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-orange-50">
      <Header />

      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-r from-orange-600 via-red-600 to-amber-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <i className="ri-leaf-line text-5xl text-white"></i>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              季節の花の楽しみ方：紅葉編
            </h1>
            <p className="text-xl text-white/90 mb-6">
              ──秋の訪れを告げる、自然の芸術──
            </p>
            <div className="flex items-center justify-center gap-4 text-white/90">
              <span className="flex items-center">
                <i className="ri-calendar-line mr-2"></i>
                2025年11月15日
              </span>
              <span className="flex items-center">
                <i className="ri-user-line mr-2"></i>
                蘭ちゃん
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/7075f9ca8c66547c2d43f2c6f9c4d865.png"
                alt="美しい紅葉"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Introduction */}
            <div className="mb-12">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                こんにちは。モモちゃん畑の蘭ちゃんです🌸<br />
                今日は、秋の風物詩である<br />
                <span className="font-bold text-orange-700">紅葉（こうよう）</span> についてお話しさせていただきますね。
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                紅葉は、花とは違う美しさを持つ自然の芸術。<br />
                緑だった葉が、赤や黄色、オレンジに染まっていく様子は、<br />
                まるで自然が描く絵画のようで、心が洗われます✨
              </p>
            </div>

            {/* Section 1 */}
            <div className="mb-12 bg-gradient-to-br from-orange-50 to-red-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-orange-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🍁</span>
                紅葉ってどうして起こるの？
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  紅葉は、秋になって気温が下がり、<br />
                  日照時間が短くなることで起こる自然現象です。
                </p>
                <p>
                  葉の中にある<span className="font-bold text-orange-700">クロロフィル（葉緑素）</span>が分解されると、<br />
                  今まで隠れていた色素が現れてくるんですね。
                </p>
                <div className="bg-white rounded-2xl p-6 my-6 border-l-4 border-orange-400">
                  <p className="text-orange-700 font-medium text-lg mb-4">
                    色の種類と仕組み
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <span className="text-red-500 mr-3 mt-1 text-xl">🔴</span>
                      <p><span className="font-bold text-red-700">赤色</span>：アントシアニンという色素が新しく作られます</p>
                    </div>
                    <div className="flex items-start">
                      <span className="text-yellow-500 mr-3 mt-1 text-xl">🟡</span>
                      <p><span className="font-bold text-yellow-700">黄色</span>：カロテノイドという色素が現れます</p>
                    </div>
                    <div className="flex items-start">
                      <span className="text-amber-500 mr-3 mt-1 text-xl">🟠</span>
                      <p><span className="font-bold text-amber-700">オレンジ</span>：赤と黄色の色素が混ざり合います</p>
                    </div>
                  </div>
                </div>
                <p className="font-medium text-orange-800">
                  自然の化学反応が生み出す美しさ。<br />
                  それが紅葉なんですね。
                </p>
              </div>
            </div>

            {/* Section 2 */}
            <div className="mb-12 bg-gradient-to-br from-red-50 to-rose-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-red-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🌳</span>
                代表的な紅葉の木々
              </h2>
              <h3 className="text-xl font-bold text-red-700 mb-6">
                ──それぞれの個性と美しさ
              </h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  日本には、美しく紅葉する木がたくさんあります。<br />
                  代表的なものをご紹介させていただきますね。
                </p>
                <div className="space-y-6 my-6">
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-red-700 mb-3 flex items-center">
                      <span className="mr-2">🍁</span>
                      モミジ（カエデ）
                    </h3>
                    <p className="text-gray-700 mb-2">
                      紅葉の代名詞とも言える美しい木。<br />
                      手のひらのような葉が、鮮やかな赤に染まります。
                    </p>
                    <p className="text-sm text-gray-600 italic">
                      見頃：10月下旬～12月上旬
                    </p>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-yellow-700 mb-3 flex items-center">
                      <span className="mr-2">🍂</span>
                      イチョウ
                    </h3>
                    <p className="text-gray-700 mb-2">
                      扇形の葉が黄金色に輝く姿は圧巻。<br />
                      落ち葉が地面を黄色い絨毯のように彩ります。
                    </p>
                    <p className="text-sm text-gray-600 italic">
                      見頃：11月中旬～12月上旬
                    </p>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-orange-700 mb-3 flex items-center">
                      <span className="mr-2">🌿</span>
                      ナナカマド
                    </h3>
                    <p className="text-gray-700 mb-2">
                      真っ赤な葉と赤い実が美しいコントラストを作ります。<br />
                      北国の紅葉の代表格です。
                    </p>
                    <p className="text-sm text-gray-600 italic">
                      見頃：9月下旬～10月中旬
                    </p>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-amber-700 mb-3 flex items-center">
                      <span className="mr-2">🍃</span>
                      ケヤキ
                    </h3>
                    <p className="text-gray-700 mb-2">
                      黄色からオレンジ、赤へと変化する美しいグラデーション。<br />
                      街路樹としても親しまれています。
                    </p>
                    <p className="text-sm text-gray-600 italic">
                      見頃：11月上旬～12月上旬
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3 */}
            <div className="mb-12 bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-amber-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">📍</span>
                美しい紅葉を見るためのポイント
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  紅葉を最も美しく楽しむためには、<br />
                  いくつかのポイントがあります。
                </p>
                <div className="space-y-6 my-6">
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                        <span className="text-2xl font-bold text-amber-700">①</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-amber-800 mb-2">
                          見頃の時期を調べる
                        </h3>
                        <p className="text-gray-700">
                          地域や標高によって見頃は異なります。<br />
                          紅葉前線の情報をチェックして、<br />
                          ベストなタイミングで訪れましょう。
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                        <span className="text-2xl font-bold text-amber-700">②</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-amber-800 mb-2">
                          朝の光を狙う
                        </h3>
                        <p className="text-gray-700">
                          朝日に照らされた紅葉は、<br />
                          透き通るような美しさがあります。<br />
                          早起きして訪れる価値がありますよ。
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                        <span className="text-2xl font-bold text-amber-700">③</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-amber-800 mb-2">
                          水辺の紅葉を探す
                        </h3>
                        <p className="text-gray-700">
                          池や川に映る紅葉は「逆さ紅葉」と呼ばれ、<br />
                          幻想的な美しさがあります。<br />
                          風のない日がおすすめです。
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                        <span className="text-2xl font-bold text-amber-700">④</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-amber-800 mb-2">
                          落ち葉も楽しむ
                        </h3>
                        <p className="text-gray-700">
                          木に付いている紅葉だけでなく、<br />
                          地面を彩る落ち葉の絨毯も美しいもの。<br />
                          足元にも目を向けてみてくださいね。
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <div className="mb-12 bg-gradient-to-br from-rose-50 to-pink-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-rose-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🎨</span>
                紅葉の楽しみ方いろいろ
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  紅葉は、見るだけでなく、<br />
                  さまざまな方法で楽しむことができます。
                </p>
                <div className="space-y-6 my-6">
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-rose-700 mb-3 flex items-center">
                      <span className="mr-2">🚶‍♀️</span>
                      紅葉狩り
                    </h3>
                    <p className="text-gray-700">
                      日本の伝統的な秋の楽しみ方。<br />
                      紅葉の名所を訪れて、自然の美しさを堪能します。<br />
                      お弁当を持って、ピクニック気分で楽しむのも素敵ですね。
                    </p>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-rose-700 mb-3 flex items-center">
                      <span className="mr-2">📸</span>
                      写真撮影
                    </h3>
                    <p className="text-gray-700">
                      紅葉は絶好の被写体。<br />
                      スマートフォンでも美しい写真が撮れます。<br />
                      逆光で撮ると、葉が透けて幻想的な雰囲気になりますよ。
                    </p>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-rose-700 mb-3 flex items-center">
                      <span className="mr-2">🍂</span>
                      押し葉作り
                    </h3>
                    <p className="text-gray-700">
                      美しい紅葉を拾って、押し葉にして保存。<br />
                      しおりやカードに使えば、秋の思い出を残せます。<br />
                      お子様との工作にもぴったりです。
                    </p>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-rose-700 mb-3 flex items-center">
                      <span className="mr-2">🍵</span>
                      紅葉を眺めながらお茶を
                    </h3>
                    <p className="text-gray-700">
                      紅葉の名所には、お茶を楽しめる場所も多くあります。<br />
                      温かいお茶を飲みながら、ゆっくりと紅葉を眺める。<br />
                      心が落ち着く、贅沢な時間です。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 5 */}
            <div className="mb-12 bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-orange-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🌡️</span>
                美しい紅葉の条件
              </h2>
              <h3 className="text-xl font-bold text-orange-700 mb-6">
                ──自然が作り出す奇跡
              </h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  紅葉が美しく色づくには、<br />
                  いくつかの気象条件が必要なんです。
                </p>
                <div className="bg-white rounded-2xl p-6 my-6">
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <span className="text-orange-500 mr-3 mt-1 text-xl">☀️</span>
                      <div>
                        <p className="font-bold text-orange-700 mb-1">日中の日差し</p>
                        <p className="text-gray-700">晴れた日が続くと、アントシアニンがよく作られます</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <span className="text-orange-500 mr-3 mt-1 text-xl">🌙</span>
                      <div>
                        <p className="font-bold text-orange-700 mb-1">朝晩の冷え込み</p>
                        <p className="text-gray-700">最低気温が8℃以下になると、紅葉が進みます</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <span className="text-orange-500 mr-3 mt-1 text-xl">💧</span>
                      <div>
                        <p className="font-bold text-orange-700 mb-1">適度な水分</p>
                        <p className="text-gray-700">乾燥しすぎず、適度な湿度があることが大切です</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <span className="text-orange-500 mr-3 mt-1 text-xl">🍃</span>
                      <div>
                        <p className="font-bold text-orange-700 mb-1">穏やかな天気</p>
                        <p className="text-gray-700">強風や台風がないと、葉が落ちずに美しく色づきます</p>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="font-medium text-orange-800">
                  これらの条件が揃ったとき、<br />
                  自然は最高の芸術作品を見せてくれるんですね。
                </p>
              </div>
            </div>

            {/* Section 6 */}
            <div className="mb-12 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-teal-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🏯</span>
                日本の紅葉文化
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  日本人は古くから紅葉を愛してきました。<br />
                  平安時代には、貴族たちが紅葉狩りを楽しみ、<br />
                  和歌にも多く詠まれています。
                </p>
                <div className="bg-white rounded-2xl p-8 my-6 text-center border-2 border-teal-200">
                  <p className="text-lg text-gray-700 mb-4 italic">
                    「奥山に 紅葉踏み分け 鳴く鹿の<br />
                    声聞く時ぞ 秋は悲しき」
                  </p>
                  <p className="text-sm text-gray-600">
                    ── 猿丸大夫（百人一首）
                  </p>
                </div>
                <p>
                  紅葉は、美しさの中に儚さを感じさせる存在。<br />
                  日本人の美意識である「もののあはれ」を<br />
                  象徴するものとして、愛され続けています。
                </p>
                <p className="font-medium text-teal-800">
                  現代でも、紅葉の名所には多くの人が訪れ、<br />
                  秋の風物詩として親しまれていますね。
                </p>
              </div>
            </div>

            {/* Section 7 */}
            <div className="mb-12 bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-pink-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🌸</span>
                蘭ちゃんからのメッセージ
              </h2>
              <h3 className="text-xl font-bold text-pink-700 mb-6">
                ──移りゆく季節の美しさ
              </h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  私は胡蝶蘭をモチーフにしたキャラクターですが、<br />
                  紅葉の美しさには、いつも心を奪われます。
                </p>
                <p>
                  花は咲いて散りますが、<br />
                  紅葉は緑から赤へと変化していく。<br />
                  その過程すべてが美しいんですね。
                </p>
                <div className="bg-white rounded-2xl p-8 my-6 text-center border-2 border-pink-200">
                  <p className="text-2xl font-bold text-pink-800 mb-4">
                    「変化することは、<br />
                    終わりではなく、新しい美しさの始まり」
                  </p>
                  <p className="text-lg text-gray-700">
                    紅葉は、そんなことを教えてくれる気がします。
                  </p>
                </div>
                <p className="font-medium text-pink-800">
                  今年の秋、ぜひ紅葉を見に出かけてみてください。<br />
                  自然が描く美しい絵画に、<br />
                  きっと心が癒されるはずです。
                </p>
                <p className="text-center text-lg font-medium text-pink-700 mt-6">
                  季節の移り変わりを感じながら、<br />
                  心豊かな時間をお過ごしくださいね🍁<br />
                  <br />
                  蘭ちゃんでした✨
                </p>
              </div>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-12">
              <span className="px-4 py-2 bg-rose-100 text-rose-700 text-sm rounded-full">
                #紅葉
              </span>
              <span className="px-4 py-2 bg-rose-100 text-rose-700 text-sm rounded-full">
                #秋の風物詩
              </span>
              <span className="px-4 py-2 bg-rose-100 text-rose-700 text-sm rounded-full">
                #紅葉狩り
              </span>
              <span className="px-4 py-2 bg-rose-100 text-rose-700 text-sm rounded-full">
                #自然の美
              </span>
              <span className="px-4 py-2 bg-rose-100 text-rose-700 text-sm rounded-full">
                #蘭ちゃん
              </span>
              <span className="px-4 py-2 bg-rose-100 text-rose-700 text-sm rounded-full">
                #季節の楽しみ方
              </span>
            </div>

            {/* Back Button */}
            <div className="text-center">
              <Link
                to="/essay"
                className="inline-flex items-center bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                エッセイ一覧に戻る
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      <section className="py-16 bg-gradient-to-br from-pink-50 to-rose-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-rose-800 mb-8 text-center">
              他のブログ記事も読む
            </h2>
            <div className="text-center">
              <Link
                to="/essay"
                className="inline-flex items-center text-rose-600 hover:text-rose-700 font-medium transition-colors"
              >
                すべての記事を見る
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
